
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, User, Shield, Settings, Bug, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useUserRoles } from "@/hooks/useUserRoles";
import { TestActions } from "@/components/admin/TestActions";

export function UserButton() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { useHasRoleQuery } = useUserRoles();
  const { data: isDeveloper } = useHasRoleQuery("DEVELOPER");
  const { data: isAdmin } = useHasRoleQuery("ADMIN");
  const { data: isTester } = useHasRoleQuery("TESTER");

  if (!isAuthenticated) {
    return (
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/login">Log in</Link>
        </Button>
        <Button asChild size="sm">
          <Link to="/register">Sign up</Link>
        </Button>
      </div>
    );
  }

  console.log("User roles in UserButton:", { isDeveloper, isAdmin, isTester });
  
  const hasAdminAccess = isDeveloper || isAdmin || isTester;
  const isFullAdmin = isDeveloper || isAdmin;

  // Function to navigate and close dropdown
  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <div className="flex items-center gap-2">
      <TestActions />
      
      {hasAdminAccess && (
        <Button
          variant="ghost"
          size="icon"
          asChild
          className="relative h-9 w-9 rounded-full"
        >
          <Link to="/admin" title={isTester && !isFullAdmin ? "Tester Area" : "Admin Panel"}>
            {isTester && !isFullAdmin ? (
              <Bug className="h-[1.2rem] w-[1.2rem] text-primary" />
            ) : (
              <Shield className="h-[1.2rem] w-[1.2rem] text-primary" />
            )}
          </Link>
        </Button>
      )}
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="relative h-9 w-9 rounded-full"
            size="icon"
          >
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary/10 text-primary">
                {user?.name ? user.name.charAt(0).toUpperCase() : <User size={16} />}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount>
          <div className="flex items-center justify-start gap-2 p-2">
            <div className="flex flex-col space-y-0.5 leading-none">
              <p className="font-medium text-sm">{user?.name || "User"}</p>
              {user?.email && (
                <p className="text-xs text-muted-foreground">{user.email}</p>
              )}
            </div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/profile" className="flex items-center">
              <User className="mr-2 h-4 w-4" />
              <span>Profile</span>
            </Link>
          </DropdownMenuItem>
          
          {hasAdminAccess && (
            <>
              <DropdownMenuItem asChild>
                <Link to="/admin" className="flex items-center">
                  {isTester && !isFullAdmin ? (
                    <>
                      <Bug className="mr-2 h-4 w-4" />
                      <span>Tester Area</span>
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-4 w-4" />
                      <span>Admin Panel</span>
                    </>
                  )}
                </Link>
              </DropdownMenuItem>
              
              {isFullAdmin && (
                <DropdownMenuItem asChild>
                  <Link to="/admin/api-integration" className="flex items-center">
                    <Database className="mr-2 h-4 w-4" />
                    <span>API Integrations</span>
                  </Link>
                </DropdownMenuItem>
              )}
            </>
          )}
          
          <DropdownMenuItem asChild>
            <Link to="/settings" className="flex items-center cursor-pointer">
              <Settings className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </Link>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer text-destructive focus:bg-destructive/10"
            onClick={logout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            <span>Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
